<template>
  <div class="modal" @click.self="$emit('closeCart')">
    <div class="cart" @click.stop>
      <font-awesome-icon icon="xmark" class="exit" @click="$emit('closeCart')" />
      <p v-show="!products.length">Your cart is empty.</p>
      <div class="cart-grid" v-if="products.length && $store.getters.allProducts.length">
        <!-- Header -->
        <div class="grid-header">
          <div class="col-name">Name</div>
          <div class="col-quantity">Quantity</div>
          <div class="col-price">Price/unit</div>
        </div>

        <!-- Products -->
        <div class="grid-row product-row" v-for="p in products" :key="p.id">
          <div class="col-name" @click="loadItem(p)">
            <h4>{{ p.name }}</h4>
            <ul>
              <span :key="modifier" v-for="modifier in p.modifiers">
                <li :key="modifier + '-' + m" v-for="m in modifier">- {{ m.name }}</li>
              </span>
            </ul>
          </div>
          <div class="col-quantity">
            <div class="quantity-controls">
              <b class="cart-symbol" @click="changeLineItemQuantityWrapper(p.hash, -1)">
                <font-awesome-icon class="quantity-icon" icon="circle-minus" />
              </b>
              <span class="quantity">{{ p.quantity }}</span>
              <b class="cart-symbol" @click="changeLineItemQuantityWrapper(p.hash, 1)">
                <font-awesome-icon class="quantity-icon" icon="circle-plus" />
              </b>
            </div>
          </div>
          <div class="col-price">${{ p.price.toFixed(2) }}</div>
        </div>
      </div>

      <!-- Surcharge row -->
      <div class="summary-row" v-if="siteSettings.surcharge">
        <div class="summary-label">
          <b>Holiday Surcharge ({{ 100 * surcharge.percentage }}%):</b>
        </div>
        <div class="summary-value">${{ surchargeTotal.toFixed(2) }}</div>
      </div>

      <!-- Credit card fee -->
      <div class="summary-row">
        <div class="summary-label">
          <b>Credit Card Fee ({{ 100 * 0.03 }}%):</b>
        </div>
        <div class="summary-value">${{ ccTotal.toFixed(2) }}</div>
      </div>

      <!-- Total -->
      <div class="summary-row total">
        <div class="summary-label"><b>Total:</b></div>
        <div class="summary-value">
          <b>${{ total.toFixed(2) }}</b>
        </div>
      </div>

      <!-- Payment methods -->
      <div class="summary-row payment" v-for="(payment, idx) in payments" :key="payment + '-' + idx">
        <div class="summary-label">
          {{ payment.type }}
          <font-awesome-icon icon="fa-trash" class="bin-icon" @click="deletePayment(idx, payment)" />
        </div>
        <div class="summary-value">${{ payment.value.toFixed(2) }}</div>
      </div>

      <!-- Paid total -->
      <div class="summary-row total">
        <div class="summary-label"><b>Paid:</b></div>
        <div class="summary-value">
          <b>${{ paid.toFixed(2) }}</b>
        </div>
      </div>

      <!-- Balance -->
      <div class="summary-row balance">
        <div class="summary-label"><b>Balance:</b></div>
        <div class="summary-value">
          <b>${{ remaining.toFixed(2) }}</b>
        </div>
      </div>

      <!-- Cart expiry timer -->
      <div v-if="products.length && timeLeft" class="cart-timer" :class="{ urgent: timeLeftSeconds < 300 }">
        <font-awesome-icon icon="clock" />
        Cart reserved for {{ timeLeft }}
      </div>
      <hr />
      <!-- Allergens -->
      <div class="customer-allergens">
        <h4>Customers Allergens</h4>
        <AllergenList :allergens="getCustomerAllergenList" :unlisted="true"/>
        <div class="order-allergens" v-if="orderAllergens">
          <h4 v-if="orderAllergens.length > 0">Allergens in your order:</h4>
          <ul>
            <li v-for="allergen in orderAllergens" :key="allergen">{{ allergen }}</li>
          </ul>

          <p v-if="conflictingAllergens">There are conflicting allergens in your order.</p>
        </div>
      </div>

      <div v-if="$parent.$options.name !== 'CheckoutModal'" class="cart-buttons">
        <button v-if="!hideCheckoutButton && products.length" class="clear-button" @click="openConfirm = !openConfirm">
          <font-awesome-icon icon="trash" class="bin-icon" />
        </button>
        <button v-if="products.length" class="primary checkout-button" @click="checkMods">Checkout</button>
      </div>
      <div>
        <button v-if="Object.keys(getWebOrder).length != 0" @click.stop.prevent="$emit('showWebOrderModal', true)" class="previous-order">Order Status</button>
      </div>
    </div>
  </div>
  <Confirm :open="openConfirm" :message="'Are you sure you want to delete your cart'" @confirmModal="confirmModal" />
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { Confirm, AllergenList } from "@monolith/components";

export default {
  data() {
    return {
      checkoutProceed: true,
      surcharge: {
        percentage: 0.15,
        total: 0,
      },
      openConfirm: false,
      openConflicting: false,
      expiresAt: null,
      timeLeft: null,
      timeLeftSeconds: 0,
      timerInterval: null,
    };
  },
  components: {
    Confirm,
    AllergenList,
  },
  props: {
    hideCheckoutButton: { type: Boolean, default: false },
    allowSplit: { type: Boolean, default: false },
  },
  emits: ["loadOrderLineItem", "showCheckoutModal", "showTab", "clearPayments", "showWebOrderModal", "closeCart"],
  mounted() {
    window.addEventListener("beamy:cart-expires-in", this.onServerExpiresIn);
    window.addEventListener("beamy:cart-cleared-by-server", this.onServerClear);
  },
  beforeUnmount() {
    window.removeEventListener("beamy:cart-expires-in", this.onServerExpiresIn);
    window.removeEventListener("beamy:cart-cleared-by-server", this.onServerClear);
    this.stopTimer();
  },
  methods: {
    checkMods() {
      if (!this.$hours.isOpenNow()) {
        this.$notify({
          text: "Shop Currently closed.",
          type: "error",
          duration: 3000,
        });
        this.$emit("closeCart");
        return;
      }

      let mods = [];
      this.checkoutProceed = true;
      this.products.forEach((prod) => {
        Object.keys(prod.modifiers).forEach((modifierset) => {
          let foundModifierSet = this.modifier_sets.find((m) => {
            return m.value._id === modifierset;
          });

          if (foundModifierSet && foundModifierSet.value.rules) {
            if (foundModifierSet.value.rules.inventory) {
              /* send to web socket */
              prod.modifiers[modifierset].forEach((mod) => {
                this.channel
                  .push("modifier:get:quantity", {
                    modifierset: modifierset,
                    modifier: mod,
                  })
                  .receive("ok", (reply) => {
                    mods.push(reply);
                    this.checkoutProceed = false;
                  });
              });
            }
          }
        });
      });
      if (this.checkoutProceed) {
        this.showCheckoutModal();
      } else {
        this.$notify({
          text: "Products currently unavailable, please choose again.",
          type: "error",
          duration: 1500,
        });
      }
    },
    deletePayment(idx, payment) {
      if (payment.type.includes("account") || payment.type.includes("voucher")) {
        const currentAccount = {
          ID: payment.id,
          name: payment.name,
          value: payment.value,
          doc_type: "credit",
          date: new Date().toISOString(),
        };
        this.channel.push(`${payment.type}:doc`, currentAccount);
      }
      this.$store.dispatch("deletePayment", idx);
    },
    changeLineItemQuantityWrapper(productHash, delta) {
      if (delta) {
        let currentProduct = this.products.find((lineItem) => lineItem.hash === productHash);
        this.changeCartDelta(currentProduct, delta);

        this.changeLineItemQuantity({ productHash: productHash, delta: delta });

        const item = this.$store.state.cart.added.find((i) => i.hash === productHash);
        if (item) {
          this.channel.push("cart:update", { product_id: productHash, quantity: item.quantity });
        } else {
          this.channel.push("cart:remove", { product_id: productHash });
        }
      }
      if (!this.products.length) {
        this.$store.dispatch("clearPayments");
      }
    },
    confirmModal(action) {
      if (action) {
        this.$store.dispatch("clearCart");
        this.channel.push("cart:clear", {});
        this.$notify({
          text: "Cart Cleared",
          type: "success",
          duration: 1500,
        });
        this.openConfirm = !this.openConfirm;
      }
    },

    changeCartDelta(currentProduct, delta) {
      Object.keys(currentProduct.modifiers).forEach((modifierset) => {
        let foundModifierSet = this.modifier_sets.find((m) => {
          return m.value._id === modifierset;
        });

        if (foundModifierSet && foundModifierSet.value.rules) {
          if (foundModifierSet.value.rules.inventory) {
            /* send to web socket */
            currentProduct.modifiers[modifierset].forEach((modifierDeltaChange) => {
              this.channel.push("modifier:set:delta", {
                modifierset: modifierset,
                modifier: modifierDeltaChange,
                delta: delta * -1, // the modifiers available is the opposite of line item quantity
              });
            });
          }
        }
      });
    },
    onServerExpiresIn(e) {
      this.startTimer(e.detail);
    },
    onServerClear() {
      this.stopTimer();
      this.$notify({ text: "Your cart was cleared by the store.", type: "warn", duration: 6000 });
    },
    startTimer(seconds) {
      this.stopTimer();
      this.expiresAt = Date.now() + seconds * 1000;
      this.tick();
      this.timerInterval = setInterval(this.tick, 1000);
    },
    stopTimer() {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
      this.timeLeft = null;
      this.timeLeftSeconds = 0;
    },
    tick() {
      const remaining = Math.max(0, Math.floor((this.expiresAt - Date.now()) / 1000));
      this.timeLeftSeconds = remaining;
      if (remaining === 0) {
        this.stopTimer();
        return;
      }
      const m = Math.floor(remaining / 60).toString().padStart(2, "0");
      const s = (remaining % 60).toString().padStart(2, "0");
      this.timeLeft = `${m}:${s}`;
    },
    ...mapActions(["changeLineItemQuantity"]),
    async showCheckoutModal() {
      //await this.$store.commit('setStripeTotalMutation', this.total * 100);
      this.$emit("showCheckoutModal", true);
    },
    loadItem(lineItem) {
      this.$emit("loadOrderLineItem", lineItem);
    },
  },
  computed: {
    ...mapGetters({
      products: "cartProducts",
      allProducts: "allProducts",
      payments: "cartPayments",
      modifier_sets: "modifier_sets",
      siteSettings: "siteSettings",
      getWebOrder: "getWebOrder",

      orderAllergens: "orderAllergens",
      getCustomerAllergenList: "getCustomerAllergenList",
      customerAllergens: "customerAllergens",
      conflictingAllergens: "conflictingAllergens",
      conflictingAllergenList: "conflictingAllergenList",
    }),
    surchargeTotal() {
      const total = this.products.reduce((total, p) => {
        return total + p.price * p.quantity;
      }, 0);
      return this.surcharge.percentage * total;
    },
    ccTotal() {
      const total = this.products.reduce((total, p) => {
        return total + p.price * p.quantity;
      }, 0);
      return 0.03 * total;
    },
    total() {
      let total = this.products.reduce((total, p) => {
        return total + p.price * p.quantity;
      }, 0);
      if (this.siteSettings.surcharge) {
        total += this.surchargeTotal;
      }
      total += this.ccTotal;
      return total;
    },
    paid() {
      return this.payments.reduce((total, p) => {
        return total + p.value;
      }, 0);
    },
    remaining() {
      return this.total - this.paid;
    },
  },
  watch: {
    "products.length"(newLen, oldLen) {
      if (newLen > oldLen) {
        this.startTimer(1800);
      } else if (newLen === 0) {
        this.stopTimer();
      }
    },
  },
};
</script>
<style scoped lang="scss">
@use "@monolith/styles";

hr {
    width: 100%;
    color: var(--text-colour);
}

.cart-timer {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  color: var(--text-colour);
  opacity: 0.7;
  padding: 0.4rem 0.5rem;
  border-radius: 0.4rem;
  background: rgba(0, 0, 0, 0.04);
  margin-top: 0.5rem;
  width: 100%;
  box-sizing: border-box;

  &.urgent {
    color: var(--warning-colour);
    opacity: 1;
    background: rgba(244, 67, 54, 0.08);
    font-weight: 600;
  }
}

.cart {
  position: relative;
  background: var(--tertiary-colour);
  border-radius: 1rem;
  overflow: hidden;
  max-height: 90vh;
  min-width: 50%;
  max-width: 75%;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 10px 20px -5px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    z-index: 1;
  }

  .cart-title {
    margin: 0;
    padding: 1.5rem 2rem 1rem;
    font-weight: 700;
    color: var(--text-colour);
    border-bottom: 2px solid rgba(var(--border-colour), 0.2);
    background: linear-gradient(135deg, rgba(var(--action-colour), 0.05), rgba(var(--action-colour), 0.02));
    flex-shrink: 0;
  }

  > p {
    text-align: center;
    padding: 5rem 2rem 4rem;
    color: var(--text-colour);
    font-size: 1.2rem;
    font-weight: 500;
    opacity: 0.7;
    margin: 0;
  }

  .exit {
    position: absolute;
    width: 98%;
    display: flex;
    justify-content: center;
    top: 0;
    padding: 0.5rem 0;
    cursor: pointer;
    color: var(--action-colour);
    z-index: 10;
    border-radius: 50%;
    font-size: 1.5rem;
  }

  .customer-allergens {
    :deep(.grid) {
      grid-template-columns: repeat(auto-fill, minmax(90px, 2fr)) !important;
      grid-auto-flow: row;
      // :deep(.allergen-list) {
      // }
    }
  }
}

.cart-symbol {
  padding: 0.5rem;
  font-size: 1.25rem;
  color: var(--action-colour);
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

// CSS Grid layout
.cart-grid {
  display: grid;
  grid-template-columns: 1fr auto auto;
  margin: 1rem 0 0 0;
  border-radius: 0.75rem;
  overflow: hidden;
  flex: 1;
  min-height: 0;
  overflow-y: auto;

  .grid-header {
    display: contents;

    > div {
      font-weight: 700;
      padding: 1rem 0.5rem;
      color: var(--text-colour);
      font-size: 0.95rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      justify-content: center;
      align-items: center;
    }
  }

  .grid-row {
    display: contents;

    > div {
      padding: 0.5rem;
      display: flex;
      align-items: baseline;
      background-color: rgba(0, 0, 0, 0.048);
      border-radius: 0.5rem;
      margin: 0.25rem;
      align-items: center;
    }

    &.payment-row {
      > div {
        font-weight: 500;
        color: var(--text-colour);
        text-transform: capitalize;
      }
    }

    &.total-row {
      > div {
        background: rgba(var(--action-colour), 0.05);
        font-weight: 500;

        b {
          font-size: 1rem;
          font-weight: 600;
          color: var(--action-colour);
        }
      }
    }
  }

  .col-name {
    flex-direction: column;
    align-items: flex-start !important;

    h4 {
      margin: 0;
      font-weight: 600;
      color: var(--text-colour);
      cursor: pointer;
    }

    &.payment {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  .col-quantity {
    justify-content: flex-start;

    .quantity-controls {
      display: flex;
      align-items: center;
    }

    .quantity-icon {
      color: var(--action-colour);
      font-size: 1rem;
    }

    span.quantity {
      display: inline-block;
      min-width: 1.5rem;
      text-align: center;
      font-weight: 600;
      font-size: 1.1rem;
      color: var(--text-colour);
      background: rgba(var(--action-colour), 0.1);
      padding: 0.25rem 0.5rem;
      border-radius: 0.5rem;
    }
  }

  .col-price {
      justify-content: flex-end;
      float: right;
    text-align: right;
    min-width: 120px;
    font-weight: 600;
    color: var(--text-colour);
  }
}

// Summary rows (totals section)
.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  border-radius: 0.5rem;
  .summary-label,
  .summary-value {
    font-size: 1rem;
    color: var(--text-colour);
  }
}
h2 {
  margin: 0;
  padding: 1.5rem 2rem 1rem;
  font-weight: 700;
  color: var(--text-colour);
  cursor: pointer;
  transition: color 0.2s ease;
  font-size: 1.5rem;

  span:nth-of-type(2) {
    color: rgba(var(--text-colour), 0.6);
    font-weight: 400;
    font-size: 1.1rem;
  }
}

h4 {
  margin: 0;
}

// Product modifiers list
ul {
  display: flex !important;
  flex-wrap: wrap;
  list-style: none;
  margin: 0.25rem 0 0 0;
  padding: 0;
  gap: 0.5rem;

  li {
    margin: 0;
    padding: 0.25rem 0.75rem;
    border-radius: 1rem;
    font-size: 0.85rem;
    color: var(--action-colour);
    font-weight: 500;
  }
}

// Modern button styling
.cart-buttons {
  display: flex;
  gap: 1rem;
  padding: 1.5rem 2rem;
  background: rgba(var(--primary-colour), 0.02);
  border-top: 1px solid rgba(var(--border-colour), 0.2);
  flex-shrink: 0;
  margin-top: auto;

  .clear-button {
    padding: 1rem;
    min-width: 4rem;
    font-size: 1.1rem;
    border: 2px solid var(--warning-colour);
    color: var(--warning-colour);
    background: rgba(var(--warning-colour), 0.05);
    border-radius: 0.5rem;
    transition: all 0.2s ease;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    .bin-icon {
      font-size: 1rem;
    }
  }

  .checkout-button {
    flex: 1;
    padding: 1rem 2rem;
    font-size: 1.1rem;
    font-weight: 600;
    border: none;
    color: white;
    background: linear-gradient(135deg, var(--action-colour), rgba(var(--action-colour), 0.8));
    border-radius: 0.5rem;
    transition: all 0.2s ease;
    cursor: pointer;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
  }
}

// Recent orders section
.recent {
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  right: 1rem;
  padding: 1rem;
  background: rgba(var(--secondary-colour), 0.9);
  backdrop-filter: blur(10px);
  border-radius: 0.75rem;
  border: 1px solid rgba(var(--border-colour), 0.2);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

// Responsive design
@media only screen and (orientation: portrait) {
  .cart {
    border-radius: 1rem;
    max-height: 100%;
    max-width: 90%;
    min-width: 90%;
    .exit {
      top: 0;
      font-size: 1.5rem;
      margin: 0.5rem 0;
    }
  }

  .cart-grid {
    font-size: 0.7rem;
    margin: 1.5rem 0 0 0;

    .grid-header > div {
      padding: 0.5rem;
      font-size: 0.65rem;
    }

    .grid-row > div {
      h4 {
        font-size: 0.8rem;
        margin-bottom: 0.25rem;
      }
    }

    .col-name {
      align-items: flex-start;
      justify-content: flex-start;
      min-width: 45%;
    }

    .col-quantity {
      justify-content: flex-start;
      min-width: 20%;
    }

    .col-price {
      justify-content: flex-end;
      min-width: 10%;
    }

    ul li {
      font-size: 0.6rem;
      padding: 0.1rem 0.4rem;
    }
  }

  .summary-row {
    padding: 0.5rem 0.75rem;

    .summary-label,
    .summary-value {
      font-size: 0.95rem;
    }
  }

  .cart-buttons {
    padding: 0.75rem;
    gap: 0.25rem;

    .clear-button {
      padding: 0.6rem;
      margin: 0.25rem;
    }

    .checkout-button {
      padding: 0.6rem 1rem;
      margin: 0.25rem;
    }
  }
}
</style>
